import { AlertTriangle, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader, Pill } from '../_primitives';
import type { MetadataFieldState } from '../postgenEvidence';
import { MetadataFieldChip } from './MetadataFieldChip';
import { AiSuggestButton } from './AiSuggestButton';

export interface SerpPreviewData {
  title: string;
  description: string;
  /** Breadcrumb-ish URL line shown above the title, e.g. "aaafence.com › blog". */
  url?: string;
}

export interface SerpPreviewCardProps {
  data: SerpPreviewData;
  onChange: (next: SerpPreviewData) => void;
  /** Soft/hard limits for meta title (Google truncates ~60). */
  titleLimit?: { warn: number; max: number };
  /** Soft/hard limits for meta description (~155–160). */
  descLimit?: { warn: number; max: number };
  readOnly?: boolean;
  /**
   * Optional per-field provenance state (E0 evidence) keyed by metadataPatch
   * field name. The mount supplies this; absent → no chips render.
   */
  fieldStates?: Record<string, MetadataFieldState>;
  /** Content ID for the per-field suggest endpoint. */
  contentId?: string;
  /** Tenant ID for the per-field suggest endpoint. */
  tenantId?: string;
  /** Disable suggest while whole-run generation is in flight. */
  metadataGenerating?: boolean;
}

type Tone = 'success' | 'warning' | 'danger';

function toneFor(len: number, warn: number, max: number): Tone {
  if (len > max) return 'danger';
  if (len > warn) return 'warning';
  return 'success';
}

/**
 * Min–max target tone (e.g. meta description 140–160): both TOO SHORT and too
 * long are flagged, and the target band reads as success — unlike toneFor,
 * which only guards the upper bound and (wrongly, for a min–max field) treats
 * an under-length value as fine.
 */
function toneForRange(len: number, min: number, max: number): Tone {
  if (len > max) return 'danger';
  if (len < min) return 'warning';
  return 'success';
}

function titleReason(len: number, warn: number, max: number): string {
  if (len > max) return `${len} chars — over ${max}; Google truncates longer titles in search results.`;
  if (len > warn) return `${len} chars — getting long; Google shows about ${max}.`;
  return `${len} chars — good length (aim for ≤${max}).`;
}

function descReason(len: number, min: number, max: number): string {
  if (len > max) return `${len} chars — over ${max}; Google truncates longer descriptions.`;
  if (len < min) return `${len} chars — a bit short; aim for ${min}–${max} for a fuller snippet.`;
  return `${len} chars — ideal length (${min}–${max}).`;
}

const COUNTER_TONE: Record<Tone, string> = {
  success: 'text-[hsl(var(--status-success))]',
  warning: 'text-[hsl(var(--status-warning))]',
  danger: 'text-destructive',
};

export function SerpPreviewCard({
  data,
  onChange,
  titleLimit = { warn: 50, max: 60 },
  descLimit = { warn: 140, max: 160 },
  readOnly = false,
  fieldStates,
  contentId,
  tenantId,
  metadataGenerating,
}: SerpPreviewCardProps) {
  const titleLen = (data.title || '').length;
  const descLen = (data.description || '').length;
  const tTone = toneFor(titleLen, titleLimit.warn, titleLimit.max);
  const dTone = toneForRange(descLen, descLimit.warn, descLimit.max);

  // "Needs review" cue: evidence IS wired (fieldStates provided) but this field
  // carries no provenance state — i.e. a generic/template value shipped without
  // an AI run or a human edit. Surfaces the already-present Suggest button
  // exactly when it's useful, and only when Suggest can actually act (contentId
  // + tenantId present, not mid-generation).
  // Lock-safe: no change to postgenEvidence.ts / MetadataFieldChip.tsx signatures.
  const canSuggest = Boolean(contentId && tenantId);
  const needsReview = (field: string, value: string): boolean =>
    canSuggest &&
    !metadataGenerating &&
    fieldStates !== undefined &&
    !fieldStates[field] &&
    (value ?? '').trim().length > 0;

  return (
    <Card railSection="serp">
      <CardHeader icon={<Eye className="size-3.5" />} title="SERP preview" />

      <div className="px-3 pb-3">
        {/* Live Google preview — uses authentic Google blue / grey palette */}
        <div className="bg-card border border-border rounded-md px-3 py-2.5 mb-3">
          <div className="text-[11px] text-[#5f6368] mb-0.5 truncate">
            {data.url || 'example.com'}
          </div>
          <div
            className="text-[14.5px] leading-snug text-[#1a0dab] font-medium mb-0.5 line-clamp-2"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {data.title || 'Untitled'}
          </div>
          <div className="text-[12px] text-[#4d5156] leading-snug line-clamp-3">
            {data.description || 'No description set.'}
          </div>
        </div>

        {/* Inputs with live counters */}
        <div className="flex flex-col gap-2.5">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                  Meta title
                </span>
                <MetadataFieldChip state={fieldStates?.meta_title} />
                {needsReview('meta_title', data.title) && (
                  <Pill tone="warning" size="sm" icon={<AlertTriangle className="size-2.5" />}>
                    Needs review
                  </Pill>
                )}
                {contentId && tenantId && (
                  <AiSuggestButton
                    field="meta_title"
                    onSuggest={(v) => onChange({ ...data, title: v })}
                    currentValue={data.title}
                    contentId={contentId}
                    tenantId={tenantId}
                    disabled={metadataGenerating}
                  />
                )}
              </span>
              <span
                className={cn('font-mono text-[10.5px] font-semibold cursor-help', COUNTER_TONE[tTone])}
                aria-live="polite"
                title={titleReason(titleLen, titleLimit.warn, titleLimit.max)}
              >
                {titleLen}/{titleLimit.max}
              </span>
            </div>
            <input
              type="text"
              value={data.title}
              onChange={(e) => onChange({ ...data, title: e.target.value })}
              readOnly={readOnly}
              placeholder="Page title shown in Google"
              className={cn(
                'w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none transition-shadow',
                'focus:border-coral focus:ring-2 focus:ring-coral/20',
                readOnly && 'opacity-70 cursor-default',
              )}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                  Meta description
                </span>
                <MetadataFieldChip state={fieldStates?.meta_description} />
                {needsReview('meta_description', data.description) && (
                  <Pill tone="warning" size="sm" icon={<AlertTriangle className="size-2.5" />}>
                    Needs review
                  </Pill>
                )}
                {contentId && tenantId && (
                  <AiSuggestButton
                    field="meta_description"
                    onSuggest={(v) => onChange({ ...data, description: v })}
                    currentValue={data.description}
                    contentId={contentId}
                    tenantId={tenantId}
                    disabled={metadataGenerating}
                  />
                )}
              </span>
              <span
                className={cn('font-mono text-[10.5px] font-semibold cursor-help', COUNTER_TONE[dTone])}
                aria-live="polite"
                title={descReason(descLen, descLimit.warn, descLimit.max)}
              >
                {descLen}/{descLimit.max}
              </span>
            </div>
            <textarea
              value={data.description}
              onChange={(e) => onChange({ ...data, description: e.target.value })}
              readOnly={readOnly}
              rows={3}
              placeholder="One- or two-sentence summary"
              className={cn(
                'w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none resize-y transition-shadow',
                'focus:border-coral focus:ring-2 focus:ring-coral/20',
                readOnly && 'opacity-70 cursor-default',
              )}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
